import React, { useState, useEffect, useRef } from 'react';
import { UserProfile } from '../../types';
import { calculatePregnancyWeek } from '../../utils/dateUtils';
import { getFullMilestoneData, MilestoneData } from '../../constants/milestoneData';
import { getPartnerTipForWeek, SUPPORT_SCRIPTS, PRACTICAL_TASKS, PARTNER_SELF_CARE } from '../../constants/partnerCoaching';
import { getWeeklySymptoms, WEEKLY_SYMPTOMS, WeeklySymptomData } from '../../constants/weeklySymptoms';
import { ChevronDown, ChevronLeft, ChevronRight, MapPin, Calendar, Clock, Star, ArrowRight, Hourglass, CheckCircle2, HelpCircle, ListChecks, Heart, MessageCircle, Sparkles, CheckSquare, Lightbulb, ExternalLink, Activity, AlertCircle, Stethoscope } from 'lucide-react';

interface JourneyProps {
    user: UserProfile;
}

// Static definition of standard care milestones to construct the timeline skeleton
const MILESTONES = [
    { week: 4, title: "Positive Test & First Call" },
    { week: 8, title: "First Ultrasound (Dating)" },
    { week: 12, title: "Nuchal Translucency Scan" },
    { week: 14, title: "Second Trimester Begins" },
    { week: 20, title: "Anatomy Scan" },
    { week: 24, title: "Glucose Screening Test" },
    { week: 28, title: "Third Trimester Begins" },
    { week: 28, title: "Tdap Vaccine" },
    { week: 32, title: "Baby Shower & Prep" },
    { week: 36, title: "Group B Strep Test" },
    { week: 37, title: "Pack Hospital Bag" },
    { week: 40, title: "Due Date" }
];

export const Journey: React.FC<JourneyProps> = ({ user }) => {
    const currentWeek = calculatePregnancyWeek(user.dueDate);
    const [selectedMilestone, setSelectedMilestone] = useState<{ week: number, title: string } | null>(null);
    const [milestoneData, setMilestoneData] = useState<MilestoneData | null>(null);

    // Partner-specific state
    const [activeView, setActiveView] = useState<'timeline' | 'symptoms' | 'coach'>('timeline');
    const weeklyTip = user.role === 'partner' ? getPartnerTipForWeek(currentWeek) : null;
    const [expandedScript, setExpandedScript] = useState<boolean>(false);
    const [expandedTask, setExpandedTask] = useState<boolean>(false);
    const [expandedSymptoms, setExpandedSymptoms] = useState<boolean>(false);

    // Symptom browser state
    const [selectedSymptomWeek, setSelectedSymptomWeek] = useState<number>(currentWeek);
    const [selectedTrimester, setSelectedTrimester] = useState<1 | 2 | 3>(
        currentWeek < 14 ? 1 : currentWeek < 28 ? 2 : 3
    );
    const weekScrollRef = useRef<HTMLDivElement>(null);

    // Get symptoms for current week
    const weeklySymptoms = getWeeklySymptoms(currentWeek);

    // Ref for auto-scroll
    const activeMilestoneRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Handle view change - scroll to top
    const handleViewChange = (view: 'timeline' | 'symptoms' | 'coach') => {
        setActiveView(view);
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    };

    // Find the active milestone (next upcoming or current)
    let activeIndex = MILESTONES.findIndex(m => m.week >= currentWeek);
    if (activeIndex === -1 && currentWeek > 0) activeIndex = MILESTONES.length - 1;
    if (activeIndex === -1) activeIndex = 0;

    useEffect(() => {
        if (activeMilestoneRef.current) {
            setTimeout(() => {
                activeMilestoneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
        }
    }, []);

    const handleMilestoneClick = (m: { week: number, title: string }) => {
        if (selectedMilestone?.title === m.title && milestoneData) {
            setSelectedMilestone(null);
            setMilestoneData(null);
            return;
        }

        setSelectedMilestone(m);
        // Get static data instantly - no API call needed
        const data = getFullMilestoneData(m.week, m.title);
        setMilestoneData(data || null);
    };

    const getTrimester = (week: number) => {
        if (week < 14) return 1;
        if (week < 28) return 2;
        return 3;
    };

    const getCountdown = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const [y, m, d] = user.dueDate.split('-').map(Number);
        const due = new Date(y, m - 1, d);

        const diffTime = due.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return { text: "Due Today!", sub: "You're ready." };
        if (diffDays < 0) return { text: `${Math.abs(diffDays)} Days Postpartum`, sub: "Welcome baby!" };

        if (diffDays < 14) return { text: `${diffDays} Days`, sub: "Almost there" };

        const weeks = Math.floor(diffDays / 7);
        return { text: `${weeks} Weeks`, sub: `${diffDays} days to go` };
    };

    const countdown = getCountdown();

    return (
        <div className="flex flex-col h-full bg-stone-50 overflow-hidden">
            {/* Compact Header */}
            <div className="bg-white px-5 py-4 border-b border-stone-100 shrink-0">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className="bg-brand-100 p-2 rounded-xl">
                            <MapPin className="w-5 h-5 text-brand-600" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-stone-900 leading-none">Journey</h1>
                            <p className="text-xs text-stone-400 mt-0.5">Week {currentWeek} • Trimester {getTrimester(currentWeek)}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-stone-900 rounded-2xl p-4 text-white shadow-lg flex items-center justify-between">
                    <div>
                        <p className="text-stone-400 text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                            <Hourglass size={10} /> Countdown
                        </p>
                        <h2 className="text-xl font-bold leading-none">{countdown.text}</h2>
                        <p className="text-stone-400 text-xs mt-1">{countdown.sub}</p>
                    </div>
                    <div className="bg-white/10 p-3 rounded-full">
                        <Calendar className="w-5 h-5 text-white" />
                    </div>
                </div>

                {/* View Toggle - available to all users */}
                <div className="flex gap-1 mt-4 bg-stone-100 p-1 rounded-xl">
                    <button onClick={() => handleViewChange('timeline')} className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-all ${activeView === 'timeline' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}>
                        <MapPin className="w-3.5 h-3.5" /> Timeline
                    </button>
                    <button onClick={() => handleViewChange('symptoms')} className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-all ${activeView === 'symptoms' ? 'bg-white text-rose-600 shadow-sm' : 'text-stone-500'}`}>
                        <Stethoscope className="w-3.5 h-3.5" /> Symptoms
                    </button>
                    {user.role === 'partner' && (
                        <button onClick={() => handleViewChange('coach')} className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-all ${activeView === 'coach' ? 'bg-white text-blue-600 shadow-sm' : 'text-stone-500'}`}>
                            <Heart className="w-3.5 h-3.5" /> Coach
                        </button>
                    )}
                </div>
            </div>

            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-6 pb-24 relative">
                {/* Timeline View */}
                {activeView === 'timeline' && (
                    <>
                        {/* Vertical Line */}
                        <div className="absolute left-9 top-6 bottom-0 w-0.5 bg-stone-200" />

                        <div className="space-y-8 relative">
                            {MILESTONES.map((m, index) => {
                                const isActive = index === activeIndex;
                                const isPast = index < activeIndex;
                                const isFuture = index > activeIndex;
                                const isSelected = selectedMilestone?.title === m.title;

                                // Simple trimester headers
                                const showTrimesterHeader = index === 0 || getTrimester(m.week) !== getTrimester(MILESTONES[index - 1].week);

                                return (
                                    <div key={index} ref={isActive ? activeMilestoneRef : null}>
                                        {showTrimesterHeader && (
                                            <div className="ml-12 mb-6 mt-2">
                                                <span className="text-xs font-bold text-stone-400 uppercase tracking-widest bg-stone-50 py-1 pr-2">
                                                    Trimester {getTrimester(m.week)}
                                                </span>
                                            </div>
                                        )}

                                        <div className={`flex gap-4 relative transition-opacity duration-500 ${isFuture ? 'opacity-60' : 'opacity-100'}`}>
                                            {/* Marker */}
                                            <div className={`
                                w-7 h-7 rounded-full border-4 shrink-0 z-10 flex items-center justify-center bg-stone-50 transition-colors
                                ${isPast ? 'border-brand-200 bg-brand-50' : ''}
                                ${isActive ? 'border-brand-500 bg-brand-500 shadow-lg shadow-brand-500/30' : ''}
                                ${isFuture ? 'border-stone-200' : ''}
                            `}>
                                                {isPast && <div className="w-2 h-2 rounded-full bg-brand-400" />}
                                                {isActive && <div className="w-2 h-2 rounded-full bg-white animate-pulse" />}
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 pb-2">
                                                <div
                                                    onClick={() => handleMilestoneClick(m)}
                                                    className={`
                                        rounded-2xl p-4 border transition-all cursor-pointer active:scale-[0.98]
                                        ${isSelected
                                                            ? 'bg-white border-brand-500 shadow-md ring-4 ring-brand-500/10'
                                                            : isActive
                                                                ? 'bg-white border-brand-200 shadow-sm ring-1 ring-brand-100'
                                                                : 'bg-white border-stone-100 hover:border-brand-200 shadow-sm'
                                                        }
                                    `}
                                                >
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-0.5">
                                                                <span className={`text-[10px] font-bold uppercase tracking-wider block ${isActive ? 'text-brand-600' : 'text-stone-400'}`}>
                                                                    Week {m.week}
                                                                </span>
                                                                {isActive && (
                                                                    <span className="text-[9px] bg-brand-100 text-brand-700 px-1.5 rounded-full font-bold">Up Next</span>
                                                                )}
                                                                {isPast && (
                                                                    <span className="text-stone-300">
                                                                        <CheckCircle2 size={12} />
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <h3 className={`font-bold text-stone-900 ${isActive ? 'text-lg' : 'text-base'}`}>{m.title}</h3>
                                                        </div>
                                                        {isSelected ? <ChevronDown className="text-brand-500 w-5 h-5" /> : <ArrowRight className="text-stone-300 w-5 h-5" />}
                                                    </div>

                                                    {/* Expanded Static Content */}
                                                    {isSelected && milestoneData && (
                                                        <div className="mt-4 pt-4 border-t border-stone-100 animate-scale-in origin-top space-y-4">
                                                            {/* Emoji + Description */}
                                                            <div className="flex items-start gap-3">
                                                                <span className="text-2xl">{milestoneData.emoji}</span>
                                                                <p className="text-sm text-stone-600 leading-relaxed flex-1">
                                                                    {milestoneData.description}
                                                                </p>
                                                            </div>

                                                            {/* Pro Tip - Role specific */}
                                                            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex gap-3 items-start">
                                                                <Star className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                                                                <div>
                                                                    <span className="text-xs font-bold text-amber-700 uppercase block mb-1">
                                                                        {user.role === 'partner' ? 'Partner Tip' : 'Your Tip'}
                                                                    </span>
                                                                    <p className="text-sm text-stone-800 italic">
                                                                        "{user.role === 'partner' ? milestoneData.partnerTip : milestoneData.pregnantTip}"
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            {/* What to Expect */}
                                                            {milestoneData.whatToExpect && milestoneData.whatToExpect.length > 0 && (
                                                                <div className="bg-purple-50 border border-purple-100 rounded-xl p-3">
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <ListChecks className="w-4 h-4 text-purple-500" />
                                                                        <span className="text-xs font-bold text-purple-700 uppercase">What to Expect</span>
                                                                    </div>
                                                                    <ul className="space-y-1">
                                                                        {milestoneData.whatToExpect.map((item, i) => (
                                                                            <li key={i} className="text-xs text-stone-600 flex items-start gap-2">
                                                                                <span className="text-purple-400 mt-0.5">•</span>
                                                                                <span>{item}</span>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}

                                                            {/* Questions to Ask */}
                                                            {milestoneData.questionsToAsk && milestoneData.questionsToAsk.length > 0 && (
                                                                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <HelpCircle className="w-4 h-4 text-blue-500" />
                                                                        <span className="text-xs font-bold text-blue-700 uppercase">Questions to Ask</span>
                                                                    </div>
                                                                    <ul className="space-y-1">
                                                                        {milestoneData.questionsToAsk.map((q, i) => (
                                                                            <li key={i} className="text-xs text-stone-600 flex items-start gap-2">
                                                                                <span className="text-blue-400 mt-0.5">•</span>
                                                                                <span>{q}</span>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}

                                                            {/* Logistics */}
                                                            <div className="flex gap-2 items-center text-xs text-stone-500 font-medium bg-stone-100 p-2 rounded-lg">
                                                                <Clock className="w-3.5 h-3.5" />
                                                                <span>{milestoneData.logistics}</span>
                                                            </div>

                                                            {/* Source Link */}
                                                            {milestoneData.source && (
                                                                milestoneData.learnMoreUrl ? (
                                                                    <a
                                                                        href={milestoneData.learnMoreUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-stone-50 rounded-lg text-xs text-stone-500 hover:text-stone-700 hover:bg-stone-100 transition-colors"
                                                                    >
                                                                        <span>Learn more: <span className="font-medium">{milestoneData.source}</span></span>
                                                                        <ExternalLink size={12} />
                                                                    </a>
                                                                ) : (
                                                                    <p className="text-xs text-stone-400">
                                                                        Source: {milestoneData.source}
                                                                    </p>
                                                                )
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* End of Road */}
                        <div className="flex gap-4 opacity-50 mt-2">
                            <div className="w-7 h-7 rounded-full border-4 border-stone-200 bg-stone-50 shrink-0 z-10" />
                            <div className="pt-0.5">
                                <span className="text-xs font-bold text-stone-400 uppercase tracking-widest">Postpartum</span>
                                <p className="text-stone-400 text-sm">The adventure continues...</p>
                            </div>
                        </div>
                    </>
                )}

                {/* Symptoms View - Browse All Weeks */}
                {activeView === 'symptoms' && (
                    <div className="space-y-4 -mt-2">
                        {/* Hero Header */}
                        <div className="bg-gradient-to-br from-rose-500 via-rose-600 to-orange-500 rounded-2xl p-5 text-white shadow-lg">
                            <div className="flex items-center gap-2 mb-1">
                                <Stethoscope className="w-5 h-5 text-rose-200" />
                                <span className="text-rose-200 text-xs font-medium uppercase tracking-wider">Symptom Guide</span>
                            </div>
                            <h2 className="text-xl font-bold mb-1">What to Expect</h2>
                            <p className="text-rose-100 text-sm">Browse pregnancy symptoms week by week</p>
                        </div>

                        {/* Trimester Tabs */}
                        <div className="flex gap-2 bg-stone-100 p-1 rounded-xl">
                            {([1, 2, 3] as const).map((t) => (
                                <button
                                    key={t}
                                    onClick={() => {
                                        setSelectedTrimester(t);
                                        // Jump to first available week in this trimester
                                        const firstWeek = WEEKLY_SYMPTOMS.find(w => w.trimester === t);
                                        if (firstWeek) setSelectedSymptomWeek(firstWeek.week);
                                    }}
                                    className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${selectedTrimester === t
                                        ? t === 1 ? 'bg-white text-purple-600 shadow-sm'
                                            : t === 2 ? 'bg-white text-emerald-600 shadow-sm'
                                                : 'bg-white text-rose-600 shadow-sm'
                                        : 'text-stone-500'
                                        }`}
                                >
                                    <span className="block text-xs opacity-60">Trimester</span>
                                    <span className="block">
                                        {t === 1 ? 'First' : t === 2 ? 'Second' : 'Third'}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Week Selector - Horizontal Scroll */}
                        <div className="relative">
                            <div
                                ref={weekScrollRef}
                                className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory"
                                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                            >
                                {WEEKLY_SYMPTOMS.filter(w => w.trimester === selectedTrimester).map((week) => {
                                    const isSelected = selectedSymptomWeek === week.week;
                                    const isCurrent = week.week === currentWeek;

                                    return (
                                        <button
                                            key={week.week}
                                            onClick={() => setSelectedSymptomWeek(week.week)}
                                            className={`
                                                flex-shrink-0 snap-center px-4 py-2.5 rounded-xl text-sm font-medium transition-all relative
                                                ${isSelected
                                                    ? selectedTrimester === 1 ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                                                        : selectedTrimester === 2 ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/30'
                                                            : 'bg-rose-600 text-white shadow-lg shadow-rose-500/30'
                                                    : 'bg-white text-stone-600 shadow-sm hover:shadow-md border border-stone-100'}
                                            `}
                                        >
                                            <span className="block text-[10px] uppercase opacity-70">Week</span>
                                            <span className="block text-lg font-bold">{week.week}</span>
                                            {isCurrent && (
                                                <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${isSelected ? 'bg-white' : 'bg-brand-500'} ring-2 ring-white`} />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            {/* Fade edges */}
                            <div className="absolute left-0 top-0 bottom-2 w-4 bg-gradient-to-r from-stone-50 to-transparent pointer-events-none" />
                            <div className="absolute right-0 top-0 bottom-2 w-4 bg-gradient-to-l from-stone-50 to-transparent pointer-events-none" />
                        </div>

                        {/* Quick Navigation */}
                        <div className="flex items-center justify-between gap-2">
                            <button
                                onClick={() => {
                                    const weeks = WEEKLY_SYMPTOMS.filter(w => w.trimester === selectedTrimester);
                                    const currentIdx = weeks.findIndex(w => w.week === selectedSymptomWeek);
                                    if (currentIdx > 0) setSelectedSymptomWeek(weeks[currentIdx - 1].week);
                                }}
                                className="flex items-center gap-1 px-3 py-2 bg-white rounded-lg shadow-sm text-stone-500 hover:text-stone-700 text-xs font-medium disabled:opacity-30"
                                disabled={selectedSymptomWeek === WEEKLY_SYMPTOMS.filter(w => w.trimester === selectedTrimester)[0]?.week}
                            >
                                <ChevronLeft className="w-4 h-4" /> Previous
                            </button>
                            {selectedSymptomWeek !== currentWeek && (
                                <button
                                    onClick={() => {
                                        setSelectedSymptomWeek(currentWeek);
                                        setSelectedTrimester(currentWeek < 14 ? 1 : currentWeek < 28 ? 2 : 3);
                                    }}
                                    className="flex items-center gap-1 px-3 py-2 bg-brand-50 text-brand-700 rounded-lg text-xs font-medium hover:bg-brand-100"
                                >
                                    <MapPin className="w-3.5 h-3.5" /> Go to Week {currentWeek}
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    const weeks = WEEKLY_SYMPTOMS.filter(w => w.trimester === selectedTrimester);
                                    const currentIdx = weeks.findIndex(w => w.week === selectedSymptomWeek);
                                    if (currentIdx < weeks.length - 1) setSelectedSymptomWeek(weeks[currentIdx + 1].week);
                                }}
                                className="flex items-center gap-1 px-3 py-2 bg-white rounded-lg shadow-sm text-stone-500 hover:text-stone-700 text-xs font-medium disabled:opacity-30"
                                disabled={selectedSymptomWeek === WEEKLY_SYMPTOMS.filter(w => w.trimester === selectedTrimester).slice(-1)[0]?.week}
                            >
                                Next <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Selected Week Content */}
                        {(() => {
                            const selectedData = getWeeklySymptoms(selectedSymptomWeek);
                            if (!selectedData) return null;

                            return (
                                <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
                                    {/* Week Header */}
                                    <div className={`p-4 ${selectedData.trimester === 1 ? 'bg-gradient-to-r from-purple-50 to-purple-100 border-b border-purple-100'
                                        : selectedData.trimester === 2 ? 'bg-gradient-to-r from-emerald-50 to-emerald-100 border-b border-emerald-100'
                                            : 'bg-gradient-to-r from-rose-50 to-rose-100 border-b border-rose-100'
                                        }`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="text-lg font-bold text-stone-900">Week {selectedData.week}</h3>
                                                <p className="text-xs text-stone-500">
                                                    {selectedData.trimester === 1 ? 'First' : selectedData.trimester === 2 ? 'Second' : 'Third'} Trimester
                                                    {selectedSymptomWeek === currentWeek && (
                                                        <span className="ml-2 px-2 py-0.5 bg-brand-100 text-brand-700 rounded-full text-[10px] font-bold">You are here</span>
                                                    )}
                                                </p>
                                            </div>
                                            <div className="text-3xl">
                                                {selectedData.symptoms[0]?.emoji || '🤰'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Symptoms List */}
                                    <div className="p-4 space-y-3">
                                        <h4 className="text-xs font-bold text-stone-500 uppercase tracking-wider flex items-center gap-2">
                                            <Activity size={12} /> Expected Symptoms
                                        </h4>
                                        <div className="space-y-3">
                                            {selectedData.symptoms.map((symptom, i) => (
                                                <div key={i} className="bg-stone-50 rounded-xl p-3 hover:bg-stone-100 transition-colors">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xl">{symptom.emoji}</span>
                                                            <div>
                                                                <span className="font-semibold text-stone-900 text-sm">{symptom.name}</span>
                                                                <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${symptom.likelihood === 'common'
                                                                    ? 'bg-rose-100 text-rose-700'
                                                                    : symptom.likelihood === 'possible'
                                                                        ? 'bg-amber-100 text-amber-700'
                                                                        : 'bg-stone-100 text-stone-600'
                                                                    }`}>
                                                                    {symptom.likelihood}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <p className="text-xs text-stone-600 mt-1.5 ml-8 leading-relaxed">{symptom.description}</p>
                                                    {symptom.relief && (
                                                        <div className="mt-2 ml-8 flex items-start gap-1.5 text-xs text-green-700 bg-green-50 rounded-lg p-2">
                                                            <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-green-600" />
                                                            <span><strong>Relief:</strong> {symptom.relief}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Body Changes */}
                                    <div className="px-4 pb-4">
                                        <div className={`rounded-xl p-4 ${selectedData.trimester === 1 ? 'bg-purple-50'
                                            : selectedData.trimester === 2 ? 'bg-emerald-50'
                                                : 'bg-rose-50'
                                            }`}>
                                            <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1 ${selectedData.trimester === 1 ? 'text-purple-700'
                                                : selectedData.trimester === 2 ? 'text-emerald-700'
                                                    : 'text-rose-700'
                                                }`}>
                                                <Sparkles size={12} /> Body Changes This Week
                                            </h4>
                                            <ul className="space-y-2">
                                                {selectedData.bodyChanges.map((change, i) => (
                                                    <li key={i} className="text-sm text-stone-700 flex items-start gap-2">
                                                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${selectedData.trimester === 1 ? 'bg-purple-400'
                                                            : selectedData.trimester === 2 ? 'bg-emerald-400'
                                                                : 'bg-rose-400'
                                                            }`} />
                                                        <span>{change}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Partner Tip */}
                                    {selectedData.partnerTip && (
                                        <div className="px-4 pb-4">
                                            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                                                <h4 className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                                                    <Heart size={12} /> {user.role === 'partner' ? 'How You Can Help' : 'Partner Tip'}
                                                </h4>
                                                <p className="text-sm text-stone-700 italic">"{selectedData.partnerTip}"</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Disclaimer */}
                                    <div className="px-4 pb-4">
                                        <p className="text-[10px] text-stone-400 flex items-start gap-1">
                                            <AlertCircle size={10} className="mt-0.5 shrink-0" />
                                            Every pregnancy is unique. Consult your healthcare provider for personalized advice.
                                        </p>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* Coach View - Condensed Single Page */}
                {user.role === 'partner' && activeView === 'coach' && weeklyTip && (
                    <div className="space-y-4 -mt-2">
                        {/* Hero Card */}
                        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 text-white shadow-lg">
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="w-5 h-5 text-blue-200" />
                                <span className="text-blue-200 text-xs font-medium uppercase tracking-wider">Week {weeklyTip.week}</span>
                            </div>
                            <h2 className="text-xl font-bold mb-2">{weeklyTip.title}</h2>
                            <p className="text-blue-100 text-sm leading-relaxed">{weeklyTip.mainTip}</p>
                        </div>

                        {/* Quick Cards Grid */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                                <div className="flex items-center gap-1.5 mb-2">
                                    <MessageCircle className="w-4 h-4 text-blue-600" />
                                    <span className="text-xs font-bold text-blue-700 uppercase">Say This</span>
                                </div>
                                <p className="text-blue-800 text-sm italic leading-snug">"{weeklyTip.sayThis}"</p>
                            </div>
                            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
                                <div className="flex items-center gap-1.5 mb-2">
                                    <Heart className="w-4 h-4 text-amber-600" />
                                    <span className="text-xs font-bold text-amber-700 uppercase">For You</span>
                                </div>
                                <p className="text-amber-800 text-sm leading-snug">{weeklyTip.selfCare}</p>
                            </div>
                        </div>

                        {/* Do This */}
                        <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
                            <div className="flex items-center gap-2 mb-3">
                                <CheckSquare className="w-4 h-4 text-green-600" />
                                <span className="font-bold text-stone-900 text-sm">Do This</span>
                            </div>
                            <div className="space-y-2">
                                {weeklyTip.doThis.map((item, i) => (
                                    <div key={i} className="flex items-start gap-2">
                                        <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs flex-shrink-0">{i + 1}</span>
                                        <span className="text-stone-600 text-sm">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Support Scripts - Collapsible */}
                        <div className="bg-white rounded-xl shadow-sm border border-stone-100 overflow-hidden">
                            <button onClick={() => setExpandedScript(!expandedScript)} className="w-full p-4 flex items-center justify-between text-left">
                                <div className="flex items-center gap-2">
                                    <MessageCircle className="w-4 h-4 text-purple-600" />
                                    <span className="font-bold text-stone-900 text-sm">What to Say</span>
                                    <span className="text-xs text-stone-400">{SUPPORT_SCRIPTS.length} scripts</span>
                                </div>
                                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${expandedScript ? 'rotate-180' : ''}`} />
                            </button>
                            {expandedScript && (
                                <div className="px-4 pb-4 space-y-2 border-t border-stone-100 pt-3">
                                    {SUPPORT_SCRIPTS.slice(0, 4).map((script, idx) => (
                                        <div key={idx} className="bg-purple-50 rounded-lg p-3">
                                            <p className="font-medium text-purple-900 text-sm mb-1">{script.situation}</p>
                                            <p className="text-purple-700 text-xs italic">"{script.whatToSay[0]}"</p>
                                        </div>
                                    ))}
                                    <p className="text-xs text-stone-400 text-center pt-1">+ {SUPPORT_SCRIPTS.length - 4} more</p>
                                </div>
                            )}
                        </div>

                        {/* Tasks - Collapsible */}
                        <div className="bg-white rounded-xl shadow-sm border border-stone-100 overflow-hidden">
                            <button onClick={() => setExpandedTask(!expandedTask)} className="w-full p-4 flex items-center justify-between text-left">
                                <div className="flex items-center gap-2">
                                    <ListChecks className="w-4 h-4 text-green-600" />
                                    <span className="font-bold text-stone-900 text-sm">Partner Checklist</span>
                                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">T{currentWeek < 14 ? '1' : currentWeek < 27 ? '2' : '3'}</span>
                                </div>
                                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${expandedTask ? 'rotate-180' : ''}`} />
                            </button>
                            {expandedTask && (
                                <div className="px-4 pb-4 space-y-2 border-t border-stone-100 pt-3">
                                    {PRACTICAL_TASKS.filter((_, i) => (currentWeek < 14 && i === 0) || (currentWeek >= 14 && currentWeek < 27 && i === 1) || (currentWeek >= 27 && i === 2))[0]?.tasks.slice(0, 5).map((task, i) => (
                                        <div key={i} className="flex items-start gap-2 bg-stone-50 p-2 rounded-lg">
                                            <div className="w-4 h-4 rounded border-2 border-stone-300 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-stone-700 text-xs">{task.task}</p>
                                                <p className="text-stone-400 text-[10px]">{task.timing}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Daily Reminder */}
                        <div className="bg-stone-900 rounded-xl p-4 text-white">
                            <div className="flex items-center gap-2 mb-2">
                                <Lightbulb className="w-4 h-4 text-amber-400" />
                                <span className="text-xs font-medium text-stone-400 uppercase">Daily Reminder</span>
                            </div>
                            <p className="text-sm text-stone-200">{PARTNER_SELF_CARE[Math.floor(currentWeek % PARTNER_SELF_CARE.length)]}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
